import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {useSelectedTab} from '@Components/tabs';
import type {GridCollectionItemBackEndData, GridCollectionItemStorage} from '@Components/base-grid/components/grid-collection-item';
import {getGridCollectionItemStorageFromBackendData} from '@Components/base-grid/components/grid-collection-item';
import {addItemsToGrid, deleteGridItems, setGridItems} from '@Components/base-grid';
import type {GridButtonItemStorage} from '@Components/base-grid/components/grid-button-item';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {FLEX_DIRECTION} from '@Utils/dom.util';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {Button, IconPlacement, Type} from '@Components/button';
import {updateCurrentCollection} from '@Panels/user-media-panel/user-media-panel-reducer';
import {makeStringUnique} from '@Utils/string.util';
import {v4 as uuidv4} from 'uuid';
import {COLLECTION_GRID_ID, UploadsMediaSubTab, UploadsMediaType, USER_UPLOADS_MEDIA_SUB_TABS, USER_UPLOADS_MEDIA_TABS} from '@Panels/user-media-panel/user-media-panel.types';
import MasonryGrid from '@Components/masonry-grid';
import {selectTab} from '@Components/tabs/tabs-reducer';
import {LoadingError} from '@Panels/user-media-panel/components/loading-error';
import type {GridSkeletonItemStorage} from '@Components/base-grid/components/grid-skeleton-item';
import {useAppDispatch, useAppSelector} from '@/hooks';

const COLLECTION_DEFAULT_HEIGHT = 48;
const COLLECTION_DEFAULT_WIDTH = 200;

export function CollectionSection(): ReactElement | null {
  const currentCollectionId = useAppSelector((state) => {
    return state.mediaUploads.collection;
  });
  const collectionItems = useAppSelector((state) => {
    return state.grids.gridsHashmap[COLLECTION_GRID_ID]?.items as Array<GridCollectionItemStorage>;
  });
  const activeTab = useSelectedTab(USER_UPLOADS_MEDIA_TABS, UploadsMediaType.IMAGE);
  const activeSubtab = useSelectedTab(USER_UPLOADS_MEDIA_SUB_TABS, UploadsMediaSubTab.UPLOADS);
  const [errorLoading, setErrorLoading] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (activeSubtab === UploadsMediaSubTab.COLLECTION) {
      void loadUserCollections();
    }
    if (currentCollectionId) {
      dispatch(selectTab(USER_UPLOADS_MEDIA_SUB_TABS, UploadsMediaSubTab.UPLOADS));
    }
  }, [activeTab, activeSubtab]);

  const getNewCollectionButton = (): GridButtonItemStorage => {
    return {
      id: 'newCollectionButton',
      type: GRID_ITEM_TYPE.BUTTON,
      text: window.i18next.t('pmwjs_new_collection'),
      icon: 'icon-plus',
      className: '',
      flexDirection: FLEX_DIRECTION.ROW,
      iconSize: IconSize.SIZE_ICON_16,
      textSize: TextSize.XSMALL,
    };
  };

  const getSkeletonItems = (skeletonItemIds: Array<string>): Array<GridSkeletonItemStorage> => {
    return skeletonItemIds.map((id) => {
      return {
        id,
        type: GRID_ITEM_TYPE.SKELETON,
        width: COLLECTION_DEFAULT_WIDTH,
        height: COLLECTION_DEFAULT_HEIGHT,
      } as GridSkeletonItemStorage;
    });
  };

  const loadUserCollections = async (): Promise<void> => {
    if (!window.PMW.isUserLoggedIn()) {
      return;
    }
    const skeletonItemIds = Array.from(Array(10)).map(() => {
      return uuidv4();
    });
    dispatch(
      setGridItems({
        id: COLLECTION_GRID_ID,
        gridSelectionGroupId: COLLECTION_GRID_ID,
        items: getSkeletonItems(skeletonItemIds),
      })
    );

    try {
      const data = (await window.PMW.readLocal('collection/getUserCollections', {
        collectionType: activeTab,
      })) as Array<GridCollectionItemBackEndData>;

      const collections = [];
      collections.push(getNewCollectionButton());
      for (const collectionData of data) {
        collections.push(getGridCollectionItemStorageFromBackendData(collectionData, activeTab));
      }

      dispatch(
        deleteGridItems({
          gridId: COLLECTION_GRID_ID,
          gridSelectionGroupId: COLLECTION_GRID_ID,
          itemIdsToDelete: skeletonItemIds,
        })
      );
      dispatch(
        setGridItems({
          id: COLLECTION_GRID_ID,
          gridSelectionGroupId: COLLECTION_GRID_ID,
          items: data.length ? collections : [],
        })
      );
    } catch (e) {
      console.error(e);
      setErrorLoading(true);
      dispatch(
        setGridItems({
          id: COLLECTION_GRID_ID,
          gridSelectionGroupId: '',
          items: [],
        })
      );
    }
  };

  const getNoCollectionsUI = (): ReactElement => {
    return (
      <div className="flex-column-align-center flex-content-center _full-height _full-width">
        <Icon icon="icon-prints-stack" type={IconType.FLAT} className="content-body" size={IconSize.SIZE_ICON_32} shape={IconShape.CIRCLE} />
        <Text className="spacing-p-t-5" val={window.i18next.t(`pmwjs_no_${activeTab.toLowerCase()}_collection_message`)} size={TextSize.SMALL} />
        <Button
          customClasses="spacing-m-t-5"
          text={window.i18next.t('pmwjs_create_a_collection')}
          type={Type.SECONDARY}
          iconPlacement={IconPlacement.LEFT}
          icon="icon-plus"
          onClick={onCreateCollection}
        />
      </div>
    );
  };

  const getCollectionNameMap = (): Record<string, number> => {
    const collectionNameMap: Record<string, number> = {};
    if (!collectionItems) {
      return collectionNameMap;
    }

    for (let i = 0; i < collectionItems.length; i++) {
      collectionNameMap[collectionItems[i].collectionName] = 1;
    }

    return collectionNameMap;
  };

  const checkDuplicateName = (newName: string): string => {
    return makeStringUnique(newName, getCollectionNameMap());
  };

  const onCreateCollection = (): void => {
    const newName = checkDuplicateName('Untitled');
    const newCollection: GridCollectionItemStorage = {
      id: uuidv4(),
      collectionId: '',
      type: GRID_ITEM_TYPE.COLLECTION,
      collectionName: newName,
      multiMediaType: activeTab,
      isShared: false,
    };
    const newCollectionItems = [];
    if (!collectionItems || !collectionItems.length) {
      newCollectionItems.push(getNewCollectionButton());
    }
    newCollectionItems.push(newCollection);
    dispatch(
      addItemsToGrid({
        id: COLLECTION_GRID_ID,
        items: newCollectionItems,
        index: 1,
      })
    );
  };

  const onCollectionGridItemClicked = (gridItem: GridItemStorage): void => {
    if (gridItem.type === GRID_ITEM_TYPE.COLLECTION) {
      dispatch(updateCurrentCollection(gridItem.id));
    } else if (gridItem.type === GRID_ITEM_TYPE.BUTTON && gridItem.id === 'newCollectionButton') {
      onCreateCollection();
    }
  };

  if (activeSubtab !== UploadsMediaSubTab.COLLECTION) {
    return null;
  }

  if (errorLoading) {
    return (
      <LoadingError
        onRefresh={(): void => {
          setErrorLoading(false);
          void loadUserCollections();
        }}
      />
    );
  }

  return (
    <>
      <MasonryGrid
        className={collectionItems?.length ? 'spacing-p-t-6 spacing-p-b-6' : ''}
        gridId={COLLECTION_GRID_ID}
        customOnGridItemClicked={onCollectionGridItemClicked}
        makeContentScrollable={false}
        showHoverOverlay={false}
        customDimensions={{
          width: COLLECTION_DEFAULT_WIDTH,
          height: COLLECTION_DEFAULT_HEIGHT,
        }}
      />
      {collectionItems?.length ? null : getNoCollectionsUI()}
    </>
  );
}
